import React, { useState, useRef, KeyboardEvent, MouseEvent } from "react";
import { Box, Popover, Button, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { t } from "i18next";

interface EditableDueDateCellProps {
  taskId: string;
  dueDate: string | Date | null | undefined;
  onChange: (taskId: string, newDate: string | null) => void;
  readOnly?: boolean;
}

const FONT_FAMILY = '"Inter Tight", "Inter", system-ui, sans-serif';
const TEXT_PRIMARY = "#1A2048";
const SOFT_SURFACE = "#F8FAFC";

const EditableDueDateCell: React.FC<EditableDueDateCellProps> = ({
  taskId,
  dueDate,
  onChange,
  readOnly = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  const hasDate = dueDate !== null && dueDate !== undefined && dueDate !== "";
  const parsedDate: Dayjs | null = hasDate ? dayjs(dueDate) : null;
  const validDate = parsedDate && parsedDate.isValid() ? parsedDate : null;

  const formattedDate = validDate ? validDate.format("D MMM YYYY") : "";
  const placeholderText = t("editableDueDateCell.setDate");
  const displayText = validDate ? formattedDate : placeholderText;

  const ariaLabel = validDate
    ? t("editableDueDateCell.ariaLabelSet", { date: formattedDate })
    : t("editableDueDateCell.ariaLabelUnset");

  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLDivElement>) => {
    if (readOnly) return;
    setAnchorEl(cellRef.current ?? (event.currentTarget as HTMLElement));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (cellRef.current) {
        setAnchorEl(cellRef.current);
      }
    }
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      onChange(taskId, newValue.format("YYYY-MM-DD"));
    }
    handleClose();
  };

  const handleClear = () => {
    onChange(taskId, null);
    handleClose();
  };

  const calendarValue: Dayjs = validDate ?? dayjs();

  return (
    <>
      <Box
        ref={cellRef}
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? undefined : 0}
        aria-label={ariaLabel}
        onClick={readOnly ? undefined : handleOpen}
        onKeyDown={readOnly ? undefined : handleKeyDown}
        sx={[{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          width: "100%",
          height: "100%",
          px: 1,
          borderRadius: "4px",
          fontFamily: FONT_FAMILY
        }, readOnly ? {
          cursor: "default"
        } : {
          cursor: "pointer"
        }, (readOnly ? {} : {
              "&:hover": {
                backgroundColor: SOFT_SURFACE,
              },
              "&:focus-visible": {
                outline: "2px solid #242C63",
                outlineOffset: "-2px",
              },
              "&:hover .editableDueDateCell-icon": {
                opacity: 1,
              },
            })]}
      >
        <Typography
          component="span"
          sx={[{
            fontFamily: FONT_FAMILY,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }, validDate ? {
            color: TEXT_PRIMARY
          } : {
            color: "text.secondary"
          }]}
        >
          {displayText}
        </Typography>
        {!readOnly && (
          <CalendarTodayOutlinedIcon
            aria-hidden="true"
            className="editableDueDateCell-icon"
            sx={{
              fontSize: 16,
              color: "text.secondary",
              opacity: 0,
              transition: "opacity 120ms ease-in-out",
              flexShrink: 0,
              marginLeft: "auto",
            }}
          />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }} slotProps={{ paper: { sx: { backgroundColor: "#ffffff" } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", p: 1.5 }}>
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
                px: 1.5,
                py: 1,
              }}
            >
              <DateCalendar
                value={calendarValue}
                onChange={handleDateChange}
                showDaysOutsideCurrentMonth
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  "& .MuiPickersCalendarHeader-root": {
                    marginTop: 0.5,
                    marginBottom: 1,
                    paddingLeft: 1,
                    paddingRight: 1,
                  },
                  "& .MuiPickersCalendarHeader-label": {
                    color: "#242C63",
                    fontSize: "1rem",
                    fontWeight: 600,
                  },
                  "& .MuiDayCalendar-header": {
                    justifyContent: "space-between",
                    marginBottom: 0.5,
                  },
                  "& .MuiDayCalendar-weekDayLabel": {
                    color: "#64748B",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    width: 36,
                    margin: "0 2px",
                  },
                  "& .MuiPickersDay-root": {
                    color: "#242C63",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    width: 36,
                    height: 36,
                    margin: "2px",
                    borderRadius: "8px",
                  },
                  "& .MuiPickersDay-root.MuiPickersDay-dayOutsideMonth": {
                    color: "#C3C7CC",
                  },
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#242C63",
                    color: "#FFFFFF",
                  },
                  "& .MuiPickersDay-root.Mui-selected:hover": {
                    backgroundColor: "#353D7A",
                  },
                  "& .MuiPickersArrowSwitcher-button": {
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#64748B",
                  },
                }}
              />
            </Box>
            {validDate && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  pt: 1,
                }}
              >
                <Button
                  size="small"
                  onClick={handleClear}
                  sx={{
                    fontFamily: FONT_FAMILY,
                    textTransform: "none",
                  }}
                >
                  {t("editableDueDateCell.clear")}
                </Button>
              </Box>
            )}
        </Box>
      </Popover>
    </>
  );
};

export default EditableDueDateCell;
